import { SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
import Swiper from 'swiper/bundle';

// Space between slides, in pixels.
const SPACE_BETWEEN = 15;

/**
 * A class for the Leadership Carousel module. Wraps a Swiper instance for the
 * team member cards. Card heights are left to CSS: the slides are flex children
 * of `.swiper-wrapper`, so `align-items: stretch` already levels them.
 */
export default class LeadershipCarousel extends MessageBus {
  /**
   * Constructor function of the LeadershipCarousel class. Must receive one
   * argument, a DOM node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    /**
     * The Swiper instance, kept so destroy() can tear it down
     * @property {?Swiper} swiper The Swiper instance created in enable()
     */
    this.swiper = null;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to create DOM references and
   * then create the Swiper instance.
   * @returns {LeadershipCarousel} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {LeadershipCarousel} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.swiperEl = this.element.querySelector(
      SELECTORS.LEADERSHIP_CAROUSEL_SWIPER
    );
    this.nextEl = this.element.querySelector(
      SELECTORS.LEADERSHIP_CAROUSEL_NEXT
    );
    this.prevEl = this.element.querySelector(
      SELECTORS.LEADERSHIP_CAROUSEL_PREV
    );

    return this;
  }

  /**
   * Create the Swiper instance to enable interaction with the view.
   * @returns {LeadershipCarousel} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.swiperEl) return this;

    this.swiper = new Swiper(this.swiperEl, {
      // 'auto' at every breakpoint lets Swiper measure each slide's real
      // rendered width (the cards are capped at 320px on mobile via CSS). With
      // a fixed slidesPerView of 1, Swiper instead forces the slide width to the
      // full container width, which disagrees with the CSS max-width and breaks
      // its end-of-carousel detection, letting users scroll into a blank slide.
      slidesPerView: 'auto',
      spaceBetween: SPACE_BETWEEN,
      navigation: {
        nextEl: this.nextEl,
        prevEl: this.prevEl
      }
    });

    return this;
  }

  /**
   * Tears the Swiper instance down.
   * @returns {LeadershipCarousel} A reference to the current instance of the class
   * @chainable
   */
  destroy() {
    if (this.swiper) this.swiper.destroy(true, true);

    return this;
  }
}
